import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from './icons';
import Colors, { withAlpha } from '../../constants/Colors';

export type SearchableSelectOption = {
  value: string;
  label: string;
  searchText?: string;
};

type SearchableSelectProps = {
  id?: string;
  value: string;
  options: SearchableSelectOption[];
  onChange: (value: string) => void;
  onOpen?: () => void;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
};

export default function SearchableSelect({
  id,
  value,
  options,
  onChange,
  onOpen,
  placeholder = 'Search...',
  emptyMessage = 'No matches found',
  disabled = false,
  style,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  const selected = useMemo(
    () => options.find((option) => option.value === value) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return options;
    return options.filter((option) => {
      const haystack = `${option.label} ${option.searchText || ''}`.toLowerCase();
      return haystack.includes(normalized);
    });
  }, [options, query]);

  const openMenu = () => {
    if (disabled) return;
    if (!open) {
      setOpen(true);
      onOpenRef.current?.();
    }
  };

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  const displayValue = open ? query : selected?.label || '';

  return (
    <div ref={containerRef} style={{ position: 'relative', ...style }}>
      <div style={{ position: 'relative' }}>
        <Search
          size={14}
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#5F719A',
            pointerEvents: 'none',
          }}
        />
        <input
          id={id}
          ref={inputRef}
          className="form-input"
          type="text"
          value={displayValue}
          disabled={disabled}
          placeholder={placeholder}
          onFocus={openMenu}
          onClick={openMenu}
          onChange={(e) => {
            setQuery(e.target.value);
            openMenu();
          }}
          style={{
            width: '100%',
            paddingLeft: 34,
            paddingRight: value || open ? 56 : 34,
          }}
        />
        <div
          style={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {value && !disabled && (
            <button
              type="button"
              aria-label="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
                setQuery('');
                setOpen(false);
              }}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 2,
                display: 'flex',
                cursor: 'pointer',
                color: '#5F719A',
              }}
            >
              <X size={14} />
            </button>
          )}
          <button
            type="button"
            aria-label="Toggle options"
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              if (open) {
                setOpen(false);
              } else {
                openMenu();
              }
            }}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 2,
              display: 'flex',
              cursor: disabled ? 'not-allowed' : 'pointer',
              color: '#5F719A',
            }}
          >
            <ChevronDown size={14} />
          </button>
        </div>
      </div>

      {open && !disabled && (
        <div
          style={{
            position: 'absolute',
            zIndex: 40,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            maxHeight: 240,
            overflowY: 'auto',
            background: '#FFFFFF',
            border: `1px solid ${withAlpha(Colors.border, 0.2)}`,
            borderRadius: 8,
            boxShadow: '0 10px 24px rgba(8, 18, 52, 0.14)',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 13, color: '#5F719A' }}>
              {emptyMessage}
            </div>
          ) : (
            filtered.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setQuery('');
                    setOpen(false);
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: isSelected ? withAlpha(Colors.accent, 0.12) : 'transparent',
                    color: Colors.text,
                    padding: '10px 12px',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = '#F7F9FF';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isSelected
                      ? withAlpha(Colors.accent, 0.12)
                      : 'transparent';
                  }}
                >
                  {option.label}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
